import React, { useState } from 'react';
import { Download, UserPlus } from 'lucide-react';
import { User, AdConfig, ContactInfo, SocialLink } from '../types';
import { QRCodeModal } from './QRCodeModal';
import { EnhancedEmailLeadForm } from './EnhancedEmailLeadForm';
import { BroadcastModal } from './BroadcastModal';
import BroadcastLoginModal from './BroadcastLoginModal';
import safeToast from '../utils/safeToast';
import CoverImage from './CoverImage';
import ProfileSection from './ProfileSection';
import ActionButtons from './ActionButtons';
import AboutSection from './AboutSection';
import ContactSection from './ContactSection';
import ContactDetails from './ContactDetails';
import { resolveUserColors } from '../utils/colors';
import { mockAds } from '../data/mockData';
import { trackAdView, trackAdClick, adRedirectUrl } from '../api/analytics';
import { listLeadsByOwner } from '../api/leads';
import { canonicalCardUrl } from '@/utils/url';
import { useShare } from '@/context/ShareProvider';
import { buildSharePayload } from '@/utils/share';

interface ECardTemplate5Props {
  user: User;
  ads: AdConfig[];
  isOwnerView?: boolean;
  onAnalyticsUpdate?: (userId: string, metric: string) => void;
  onLeadSubmit?: (leadData: any) => void;
  leads?: any[];
  onContactExchange?: () => void;
}

export const ECardTemplate5: React.FC<ECardTemplate5Props> = ({ user, ads, isOwnerView = false, onAnalyticsUpdate, onLeadSubmit, leads = [], onContactExchange }) => {
  const [showQRModal, setShowQRModal] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [authedLeads, setAuthedLeads] = React.useState<any[] | null>(null);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const { openShare } = useShare();

  // Check if broadcast should be enabled (Gold users viewing their own card)
  const _exp = user.subscription?.expiresAt ? new Date(user.subscription.expiresAt as any) : null;
  const _notExpired = !_exp || _exp > new Date();
  const _subActive = (user.subscription?.isActive ?? true) && _notExpired;
  const isBroadcastEnabled = isOwnerView && _subActive && (user.subscription?.type === 'gold' || user.subscription?.type === 'platinum');
  const isBroadcastOwnerEnabled = isBroadcastEnabled;

  const userColors = resolveUserColors(user.colors as any);

  const sourceAds = React.useMemo(() => {
    const base = Array.isArray(ads) ? [...ads] : [];
    if (base.length === 0) return mockAds;
    if (base.length >= 3) return base;
    const out = [...base];
    try {
      const mocks = (mockAds as any[]).filter((m:any) => m && m.isActive !== false);
      for (const m of mocks) {
        if (out.length >= 3) break;
        const dupe = out.find((x:any) =>
          String(x?.title || '').toLowerCase() === String(m?.title || '').toLowerCase() ||
          String(x?.link || '') === String(m?.link || '')
        );
        if (!dupe) out.push(m as any);
      }
    } catch {}
    return out;
  }, [ads]);
  const activeAds = sourceAds.filter(ad => ad.isActive);
  const currentAd = activeAds.length > 0 ? activeAds[currentAdIndex] : null;
  const currentAdHref = React.useMemo(() => {
    try {
      const id = (currentAd as any)?._id || (currentAd as any)?.id || (currentAd as any)?.adId;
      const href = (currentAd as any)?.link || (currentAd as any)?.url || (currentAd as any)?.href;
      if (id && href) return adRedirectUrl(String(id), String(href));
      return String(href || '#');
    } catch { return '#'; }
  }, [currentAd, currentAdIndex]);

  // Auto-scroll ads every 5 seconds
  React.useEffect(() => {
    if (activeAds.length > 1) {
      const interval = setInterval(() => {
        setCurrentAdIndex((prevIndex) => (prevIndex + 1) % activeAds.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [activeAds.length]);

  // Track ad view when current visible ad changes (skip mock ads without ids)
  React.useEffect(() => {
    try {
      const id = (currentAd as any)?._id || (currentAd as any)?.id || (currentAd as any)?.adId;
      if (id) {
        try { trackAdView(String(id)).catch(() => {}); } catch {}
      }
    } catch {}
  }, [currentAdIndex]);

  const handleQR = () => {
    setShowQRModal(true);
    onAnalyticsUpdate?.(user.id, 'qrScans');
  };
  
  const handleShare = () => {
    const handle = (user as any)?.handle || (user as any)?.username || '';
    const username = (user as any)?.username || (user as any)?.id || '';
    const fallbackUrl = typeof window !== 'undefined' ? window.location.href : '';
    const cardUrl = (handle && username) ? canonicalCardUrl(handle, username) : (user.website || fallbackUrl);
    const payload = buildSharePayload({ name: user.name, url: cardUrl });
    const isMobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone|webOS/i.test(navigator.userAgent||'');
    if ((navigator as any)?.share && isMobile) {
      (navigator as any).share(payload).catch(()=> openShare(cardUrl, payload.text));
    } else {
      openShare(cardUrl, payload.text);
    }
  // Single increment path; avoid double counting shares
  onAnalyticsUpdate?.(user.id, 'shares');
  };
  
  const handleBroadcast = () => {
    // Only Gold/Platinum subscribers should see login for ownership verification
    if (_subActive && (user.subscription?.type === 'gold' || user.subscription?.type === 'platinum')) {
      setShowLoginModal(true);
      return;
    }
    // Non-eligible: show soft token message only
    try { safeToast('info', 'This function only available for Gold or Platinum User, Subscribe Now !'); } catch {}
  };
  
  const handleEmail = () => {
    setShowEmailForm(true);
    onAnalyticsUpdate?.(user.id, 'messageClicks');
  };

  const handleSaveCard = () => {
    // Create vCard data
    const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:${user.name}
ORG:${user.company}
TITLE:${user.position}
TEL:${user.phone}
EMAIL:${user.email}
URL:${user.website}
NOTE:${user.bio}
END:VCARD`;

    // Create blob and download
    const blob = new Blob([vCardData], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
  link.download = `${(user.name || '').replace(/\s+/g, '-')}.vcf`;
    link.click();
    URL.revokeObjectURL(url);
    
    onAnalyticsUpdate?.(user.id, 'saves');
  };

  // Create ContactInfo object for components
  const contactInfo: ContactInfo = {
    name: user.name,
    title: `${user.position} at ${user.company}`,
    company: user.company,
    avatar: user.avatar,
    phone: user.phone,
    email: user.email,
    website: user.website,
    bio: user.bio,
    location: "Dar es Salaam, Tanzania"
  };

  // Create social links array (guard against missing user.social)
  const allowedSocials = new Set(['linkedin', 'twitter', 'instagram', 'facebook', 'tiktok', 'x']);
  const normalize = (s: string) => String(s || '').trim().toLowerCase();
  const alias = (p: string, url?: string) => {
    const k = normalize(p);
    const u = normalize(url || '');
    if (k === 'x' || k.includes('x.com') || u.includes('x.com')) return 'twitter';
    if (k.includes('twitter') || u.includes('twitter.com')) return 'twitter';
    return k;
  };
  const socialLinks: SocialLink[] = Object.entries((user as any).socialLinks || (user as any).social || {})
    .filter(([platform, url]) => url && allowedSocials.has(normalize(platform)))
    .map(([platform, url]) => ({ platform: alias(String(platform), String(url)), url: url as string }));

  return (
    <>
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
  {/* Cover Image Component: accept either coverImage or coverUrl to ensure visibility across contexts */}
  <CoverImage src={(user as any).coverImage || (user as any).coverUrl} alt="Cover" />

        {/* Customizable slim line below cover */}
        <div 
          className="h-1 w-full"
          style={{ backgroundColor: userColors.stripLine }}
        ></div>

        {/* Profile Section using new component */}
        <ProfileSection contactInfo={contactInfo} user={user} />

        {/* Action Buttons using new component */}
        <ActionButtons
          contactInfo={contactInfo}
          onShowQR={handleQR}
          onShowShare={handleShare}
          onShowBroadcast={handleBroadcast}
          onShowEmail={handleEmail}
          isBroadcastEnabled={isBroadcastEnabled}
          onAnalyticsUpdate={(metric) => onAnalyticsUpdate?.(user.id, metric)}
          onContactExchange={!isOwnerView ? onContactExchange : undefined}
        />

        {/* About Section using new component */}
        <AboutSection description={user.bio} />

        {/* Contact Section using new component */}
        <ContactSection
          contactInfo={contactInfo}
          socialLinks={socialLinks}
          user={user}
          onShowEmail={handleEmail}
          onAnalyticsUpdate={(metric) => onAnalyticsUpdate?.(user.id, metric)}
        />

        {/* Alternative: Contact Details (uncomment to use instead of ContactSection) */}
        {/* <ContactDetails contactInfo={contactInfo} /> */}

        {/* Contact Exchange Button */}
        {onContactExchange && !isOwnerView && (
          <div className="px-6 pb-2">
            <button
              onClick={onContactExchange}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-full transition-colors shadow-lg text-sm"
            >
              <UserPlus className="w-4 h-4" />
              Send {user.name?.split(' ')[0] || 'them'} your contact
            </button>
          </div>
        )}

        {/* Save Contact Button */}
        <div className="px-6 pb-6">
          <button 
            onClick={handleSaveCard}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-red-700 hover:bg-red-800 text-white font-medium rounded-full transition-colors shadow-lg text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M5 20h14" />
            </svg>
            Save to Contacts
          </button>
        </div>

        {/* Sponsored Section */}
        <div className="px-6 pb-6 bg-gray-50 mx-6 mb-6 rounded-2xl">
          <div className="text-center pt-4 relative overflow-hidden">
            <p className="text-xs text-gray-500 mb-4 uppercase tracking-wider font-medium">
              SPONSORED
            </p>
            {currentAd ? (
              <div className="relative">
                <a 
                  href={currentAdHref} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="block transition-all duration-500 ease-in-out"
                  key={currentAdIndex}
                  onClick={() => { try { const id = (currentAd as any)?._id || (currentAd as any)?.id || (currentAd as any)?.adId; if (id) { try { trackAdClick(String(id)).catch(() => {}); } catch {} } } catch {} }}
                >
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-gray-400 transition-all duration-500 bg-white" style={{ minHeight: 170 }}>
                    <img 
                      src={currentAd.image} 
                      alt={currentAd.title}
                      className="w-full h-20 object-cover rounded mb-3 transition-opacity duration-500"
                    />
                    <h4 className="font-semibold text-gray-800 mb-1 transition-opacity duration-500 truncate" title={currentAd.title}>{currentAd.title}</h4>
                    <p className="text-sm text-gray-600 transition-opacity duration-500 line-clamp-2" title={currentAd.description}>{currentAd.description}</p>
                  </div>
                </a>
                
                {/* Ad indicators */}
                {activeAds.length > 1 && (
                  <div className="flex justify-center mt-3 space-x-2">
                    {activeAds.map((_, index) => (
                      <div
                        key={index}
                        className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                          index === currentAdIndex ? 'bg-gray-600' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-gray-400 bg-white">
                <div className="text-lg font-medium mb-2 text-gray-300">Advertisement Space</div>
                <div className="text-sm text-gray-400">Your ad could be here</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        user={user}
      />

      <EnhancedEmailLeadForm
        isOpen={showEmailForm}
        onClose={() => setShowEmailForm(false)}
        recipientName={user.name}
        recipientEmail={user.email}
        cardOwnerId={user.id}
        onLeadSubmit={onLeadSubmit}
      />

      <BroadcastModal
        isOpen={showBroadcastModal}
        onClose={() => setShowBroadcastModal(false)}
        userName={user.name}
        cardOwnerId={user.id}
        leads={(authedLeads ?? leads) as any}
      />
      <BroadcastLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={async ({ id }) => {
          const uid = String(user.id);
          if (String(id) !== uid) { safeToast('error', 'This account does not own this card.'); return; }
          const exp = user.subscription?.expiresAt ? new Date(user.subscription.expiresAt as any) : null;
          const active = (user.subscription?.isActive ?? true) && (!exp || exp > new Date());
          if (!active || !(['gold','platinum'].includes(String(user.subscription?.type)))) {
            safeToast('warning', 'Broadcast requires an active Gold or Platinum subscription.');
            return;
          }
          try { const fresh = await listLeadsByOwner(String(uid)).catch(() => null); if (fresh && Array.isArray(fresh)) setAuthedLeads(fresh as any); } catch {}
          setShowBroadcastModal(true);
          try { onAnalyticsUpdate?.(user.id, 'messageClicks'); } catch {}
        }}
      />
  {/* share popup handled by global ShareProvider */}
    </>
  );
};